import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Salvaged from ui-sprocket src/components/toast/ToastProvider.tsx — reviewed 2026-09-21:
// icons come from the package's icon set; the sidebar-width CSS variable is the one
// `AppShell` publishes; `exactOptionalPropertyTypes`-safe `undo` handling; otherwise unchanged.
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Slide, {} from '@mui/material/Slide';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { CheckCircleIcon, CloseIcon, ErrorIcon, InfoIcon } from '../../icons.js';
import { PHONE_BREAKPOINT } from '../../tokens/breakpoints.js';
import { RADIUS } from '../../tokens/radius.js';
import { SIDEBAR_WIDTH_VAR } from '../app-shell/constants.js';
import { ToastContext } from './toast-context.js';
import { initialToastState, toastReducer } from './toast-state.js';
const ICONS = {
    success: _jsx(CheckCircleIcon, { fontSize: "small" }),
    error: _jsx(ErrorIcon, { fontSize: "small" }),
    info: _jsx(InfoIcon, { fontSize: "small" }),
};
function SlideUp(props) {
    return _jsx(Slide, { ...props, direction: "up" });
}
/**
 * Bottom-left on desktop, above the tab bar on phones (§6). One at a time; an Undo button
 * when the action can be reverted. Mounted inside the theme so it carries the brand's
 * focus ring and the neutral ramp.
 */
export function ToastProvider({ children }) {
    const [state, dispatch] = useReducer(toastReducer, initialToastState);
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down(PHONE_BREAKPOINT));
    const current = state.queue[0];
    const api = useMemo(() => ({
        success: (message, options) => dispatch({
            type: 'push',
            tone: 'success',
            message,
            ...(options?.undo ? { undo: options.undo } : {}),
        }),
        error: (message) => dispatch({ type: 'push', tone: 'error', message }),
        info: (message) => dispatch({ type: 'push', tone: 'info', message }),
    }), []);
    const dismiss = useCallback(() => {
        if (current)
            dispatch({ type: 'dismiss', id: current.id });
    }, [current]);
    // Self-dismiss. A new toast restarts the clock because `current` changes.
    useEffect(() => {
        if (!current)
            return;
        const id = window.setTimeout(dismiss, current.duration);
        return () => window.clearTimeout(id);
    }, [current, dismiss]);
    const toneColor = current?.tone === 'error'
        ? 'error.main'
        : current?.tone === 'success'
            ? 'success.main'
            : 'info.main';
    return (_jsxs(ToastContext.Provider, { value: api, children: [children, _jsx(Snackbar, { open: Boolean(current), anchorOrigin: { vertical: 'bottom', horizontal: isPhone ? 'center' : 'left' }, slots: { transition: SlideUp }, sx: {
                    // Clear the phone tab bar and the home indicator; on desktop, clear the sidebar
                    // (AppShell publishes its width as a CSS variable while it is mounted). The
                    // doubled selector outranks the anchor-origin media query MUI sets on the root.
                    '&&': {
                        bottom: isPhone ? 'calc(72px + env(safe-area-inset-bottom))' : 24,
                        left: isPhone ? 16 : `calc(var(${SIDEBAR_WIDTH_VAR}, 0px) + 24px)`,
                        right: isPhone ? 16 : 'auto',
                    },
                }, children: _jsxs(Paper, { role: current?.tone === 'error' ? 'alert' : 'status', "data-tone": current?.tone, elevation: 0, sx: (t) => ({
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        pl: 1.5,
                        pr: 0.5,
                        py: 0.75,
                        minWidth: 280,
                        maxWidth: 480,
                        borderRadius: `${RADIUS.md}px`,
                        border: `1px solid ${t.palette.hairline}`,
                        boxShadow: t.customShadows.overlay,
                        bgcolor: 'background.paper',
                    }), children: [_jsx(Box, { sx: { color: toneColor, display: 'flex' }, "aria-hidden": "true", children: current ? ICONS[current.tone] : null }), _jsx(Typography, { variant: "body2", sx: { flexGrow: 1, fontWeight: 500, py: 0.5 }, children: current?.message }), current?.undo && (_jsx(Button, { size: "small", onClick: () => {
                                current.undo?.();
                                dismiss();
                            }, sx: { color: 'brand.text', flexShrink: 0 }, children: "Undo" })), _jsx(IconButton, { size: "small", onClick: dismiss, "aria-label": "Dismiss", children: _jsx(CloseIcon, { sx: { fontSize: 16 } }) })] }) }, current?.id)] }));
}
//# sourceMappingURL=toast-provider.js.map